// @ts-strict-ignore
import * as React from 'react';
import styles from '../styles/stepStyles';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { FeatureSection } from '../../typescriptHelpers';
import { ArrowRightOutlined, EditOutlined } from '@ant-design/icons';
import {
    appleAppIsReadyToRelease,
    getAutomationIdByType,
    getIsAppleAppStore,
    getIsSamsungGalaxyStore,
    innerStepStatusColorForReleaseStepStatus,
    isAndroidPlatform,
    isExpoEASRelease,
    isAppReleased,
    isPhasedRelease,
    isPhasedReleaseAcceleratedByRunway,
    getStabilityInfos,
    isPhasedReleaseComplete,
    isPhasedReleasePaused,
    isPhasedReleasePausedByRunway,
    shouldShowLastUploadedBuilds,
} from '../../helpers';
import { usePhasedReleaseState } from '@/utils/hooks/usePhasedReleaseState';
import {
    AmazonAppStoreData,
    AmazonAppStoreState,
    AndroidAutoRolloutStartStrategy,
    AppleAppStoreData,
    AppleAppStorePhasedReleaseState,
    AppleAppStoreReleaseType,
    AppleAppStoreState,
    AppPlatform,
    AppStoreReviewSubmissionItemState,
    AppStoreType,
    AutomationAutoRelease,
    AutomationAutoStagedRollout,
    AutomationAutoSubmitRelease,
    AutomationType,
    AutoReleaseStrategy,
    GooglePlayReleaseLifecycleState,
    GooglePlayStoreData,
    GooglePlayStoreState,
    HuaweiAppStoreData,
    PhasedRelease,
    ReleaseStatus,
    ReleaseStepStatus,
    ReleaseStepTakeoff,
    ReleaseStepTakeoffData,
    RunwayResponseType,
    SamsungAppStoreData,
    SamsungAppStoreState,
} from '../../types';
import { antGrey, releaseStepModuleTitleStyle, runwayColors } from '../styles/globalStyles';
import { AutomatedTakeoffTasksBase, CreatedTagInfo } from '../AutomatedTakeoffTasks';
import { dayjs } from '@/extendedDayjs';
import { displayConsoleNameForAppStoreType, displayStoreNameForAppStoreType } from '../../helpers/displayHelpers';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import Layout, { Content, Footer } from 'antd/lib/layout/layout';
import { DatetimeFormat } from '../../timeHelpers';
import { SelectedAppStoreBuildModuleEmbedded } from '../SelectedAppStoreBuildModule';
import Button from 'antd/lib/button';
import { iconForPlatform } from '../../helpers/platformComponentHelpers';
import { ReleaseUpdatable } from '../../actions/updatable';
import { ReleaseAppleVersionRequestReleaseStep, tryUpdateRelease } from '../../actions/release';
import TrackedButton from '../../helpers/TrackedButton';
import * as AnalyticEvents from '../../utils/analytics';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import {
    selectAppPlatform,
    selectAppStoreIntegration,
    selectAppStoreType,
    selectAutomationById,
    selectAutoSubmitStepAutomationRun,
    selectHasVCSIntegrationConnected,
    selectIsAutomationEnabledForReleaseType,
    selectIsReleaseRollback,
    selectLatestVersion,
    selectOrg,
    selectRelease,
    selectReleaseIsRunwayAutomated,
    selectReleaseStep,
    selectReleaseSuccessfullyRunwayAutomated,
    selectRolloutStepRunIdForDestinationStepRun,
    selectStabilityIntegrations,
    selectStepRun,
} from '../../selectors/selectors';
import { createStepRunRoute } from '@/helpers/routeHelpers';
import { Link } from 'react-router-dom';
import { ReleaseEventsTimelineSection } from '../releaseOverview/ReleaseTimelineSection';
import { ReleaseTimelineSectionContext } from '../timelineEvents/EventsTimeline';
import { EmptyStateWrapper, useIsInEmptyState } from '../releaseEmptyState/EmptyStateWrapper';
import { globalMessage } from '@/utils/globalMessage';
import { AppStoreCollapsibleItem, AppStoreItemsCollapse } from '@/components/AppStoreItems/AppStoreItemsCollapse';
import { iconAndAltForIntegrationType } from '@/helpers/integrationHelpers';
import { compact } from 'lodash';
import AutomatedByRunwayIcon from '@/components/AutomatedByRunwayIcon';
import { ReleaseSummaryMessageComponentBase } from '@/components/releaseSteps/ReleaseSummaryMessageComponent';
import {
    StabilityMetricsContainerBase,
    ViewInStabilityPlatformButton,
} from '@/components/selectedAppStoreBuild/StabilityMetricsContainer';
import PhasedReleaseCard from '@/components/monitoring/PhasedReleaseCard';
import { PhasedReleaseInlineChart } from '@/components/StepContentTakeoff/PhasedReleaseInlineChart';
import { StabilityMetricsInlinePercentages } from '@/components/StepContentTakeoff/StabilityMetricsInlinePercentages';
import { renderReviewsSubmissionItemCard } from '@/components/StepContentSubmission/helpers';
import { LastUploadedAppStoreBuildModuleEmbedded } from '@/components/LastUploadedAppStoreBuildModule';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { ReleaseStepOrStepRunComponentProps } from '@/components/ValidatedRouteComponents';
import { ReleaseAppleVersionRequest, StepRunUpdatable } from '@/actions/stepRun';
import { ReleaseDelayDescription, showsReleaseDelay } from '../sharedComponents/ReleaseDelayDescription';
import { AppStoreReleaseConfirmationModal } from './AppStoreReleaseConfirmationModal';
import { ExpoEASReleaseStep } from './ExpoEASReleaseStep';

const titleForState = (
    appStoreType: AppStoreType,
    isReleased: boolean,
    isPhased: boolean,
    status: ReleaseStepStatus,
    isPhasedReleasePausedByRunway: boolean,
    isReleaseFromScraper: boolean,
    appleAppStoreData?: AppleAppStoreData,
    googleStoreData?: GooglePlayStoreData,
    needsManualRolloutStart?: boolean,
    phasedReleaseHasBeenSupersededByNewRelease?: boolean,
    enablePlayReleaseLifecycle?: boolean,
) => {
    if (isReleaseFromScraper) {
        return 'Released';
    }

    switch (status) {
        case ReleaseStepStatus.Inactive:
        case ReleaseStepStatus.Skipped:
            return 'Not ready to release';
        case ReleaseStepStatus.Pending:
        case ReleaseStepStatus.PendingChecklistOnly:
        case ReleaseStepStatus.Ready:
            switch (appStoreType) {
                case AppStoreType.Apple:
                    return appleTitleFromAppStoreData(
                        appleAppStoreData,
                        isReleased,
                        isPhased,
                        isPhasedReleasePausedByRunway,
                        phasedReleaseHasBeenSupersededByNewRelease,
                    );
                case AppStoreType.Google:
                    return googleTitleFromAppStoreData(
                        isReleased,
                        isPhased,
                        googleStoreData?.state,
                        isPhasedReleasePausedByRunway,
                        needsManualRolloutStart,
                        enablePlayReleaseLifecycle,
                        googleStoreData?.releaseLifecycleState,
                    );
                case AppStoreType.Amazon:
                    return amazonTitleFromAppStoreData(isReleased);
                case AppStoreType.Huawei:
                    return huaweiTitleFromAppStoreData(isReleased);
                case AppStoreType.Samsung:
                    return samsungTitleFromAppStoreData(isReleased);
                default:
                    return appStoreType;
            }
        case ReleaseStepStatus.Blocked:
            // Currently not possible
            return '';
    }
};

const descriptionForState = (
    platform: AppPlatform,
    appStoreType: AppStoreType,
    isReleased: boolean,
    isReleasedByRunway: boolean,
    autoReleaseStrategy: AutoReleaseStrategy | undefined,
    isPhased: boolean,
    status: ReleaseStepStatus,
    isPhasedReleasePausedByRunway: boolean,
    isPhasedReleaseAcceleratedByRunway: boolean,
    isCustomStagedRolloutEnabled: boolean,
    isReleaseFromScraper: boolean,
    phasedRelease: PhasedRelease | null,
    appleAppStoreData?: AppleAppStoreData,
    googleStoreData?: GooglePlayStoreData,
    amazonAppStoreData?: AmazonAppStoreData,
    needsManualRolloutStart?: boolean,
    phasedReleaseHasBeenSupersededByNewRelease?: boolean,
    enablePlayReleaseLifecycle?: boolean,
) => {
    const storeText = displayStoreNameForAppStoreType(appStoreType);

    if (isReleaseFromScraper) {
        return `Your update has been successfully released to the ${storeText}.`;
    }

    if (
        isPhased &&
        isPhasedReleasePausedByRunway &&
        phasedRelease.changedByAutomation === AutomationType.AutoStagedRollout
    ) {
        return isAndroidPlatform(platform) ?
                `Rollout sequence has been paused by Runway during a scheduled blackout period.`
            :   `Phased release has been paused by Runway during a scheduled blackout period.`;
    }

    switch (appStoreType) {
        case AppStoreType.Apple:
            return appleDescriptionFromAppStoreData(
                isReleased,
                isReleasedByRunway,
                autoReleaseStrategy,
                isPhased,
                status,
                isPhasedReleasePausedByRunway,
                isPhasedReleaseAcceleratedByRunway,
                appleAppStoreData,
                phasedReleaseHasBeenSupersededByNewRelease,
            );
        case AppStoreType.Google:
            return googleDescriptionFromAppStoreData(
                isReleased,
                isReleasedByRunway,
                isPhased,
                isPhasedReleasePausedByRunway,
                isPhasedReleaseAcceleratedByRunway,
                isCustomStagedRolloutEnabled,
                needsManualRolloutStart,
                googleStoreData,
                enablePlayReleaseLifecycle,
            );
        case AppStoreType.Amazon:
            return amazonDescriptionFromAppStoreData(isReleased, isReleasedByRunway, amazonAppStoreData);
        case AppStoreType.Huawei:
            return huaweiDescriptionFromAppStoreData(isReleased, isReleasedByRunway);
        case AppStoreType.Samsung:
            return samsungDescriptionFromAppStoreData(isReleased, isReleasedByRunway);
        default:
            return appStoreType;
    }
};

const titleColor = (
    status: ReleaseStepStatus,
    isReleased: boolean,
    isPhased: boolean,
    isPhasedReleaseComplete: boolean,
    isReleaseFromScraper: boolean,
) => {
    if (isReleaseFromScraper) {
        return runwayColors.statusGrey;
    }

    if (isReleased && isPhased) {
        if (isPhasedReleaseComplete) {
            return runwayColors.statusGreen;
        } else {
            return runwayColors.statusYellow;
        }
    }

    return innerStepStatusColorForReleaseStepStatus(status);
};

const appStoreButtonText = (appStoreType: AppStoreType) => {
    const consoleName = displayConsoleNameForAppStoreType(appStoreType);
    const consoleLinkText = `${consoleName}`;

    return consoleLinkText;
};

export const StepContentTakeoff: React.FC<ReleaseStepOrStepRunComponentProps> = (props) => {
    const stepRun = useSelector((state: RootState) => selectStepRun(state, props.stepRunComponentProps?.stepRunId));
    const stepData: ReleaseStepTakeoffData | undefined =
        stepRun ? { ...stepRun.data.summaryData, ...stepRun.data.complementaryData } : undefined;
    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, props.appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));
    const release = useSelector((state: RootState) => selectRelease(state, props.releaseId));
    const isExpoRelease = isExpoEASRelease(stepData, appStoreType, appPlatform, stepRun?.destinations);

    if (isExpoRelease && props.stepRunComponentProps && stepData) {
        return (
            <ExpoEASReleaseStep
                orgId={props.orgId}
                appId={props.appId}
                releaseId={props.releaseId}
                displayableVersion={release.displayableVersion}
                stepRunComponentProps={props.stepRunComponentProps}
                expoData={stepData.expoEas}
            />
        );
    }

    return <NativeStepContentTakeoff {...props} />;
};

const NativeStepContentTakeoff: React.FC<ReleaseStepOrStepRunComponentProps> = ({
    orgId,
    appId,
    releaseId,
    releaseStepType,
    stepRunComponentProps,
}) => {
    const userId = useSelector((state: RootState) => state.user.id);
    const release = useSelector((state: RootState) => selectRelease(state, releaseId));
    const releaseStep = useSelector((state: RootState) =>
        selectReleaseStep(state, releaseId, releaseStepType),
    ) as ReleaseStepTakeoff;
    const releaseVersion = release.version;
    const displayableVersion = release.displayableVersion;

    const stepRunId = stepRunComponentProps?.stepRunId;
    const stepRun = useSelector((state: RootState) => selectStepRun(state, stepRunId));
    const stepData: ReleaseStepTakeoffData =
        stepRun ? { ...stepRun.data.summaryData, ...stepRun.data.complementaryData } : releaseStep.data;
    const platform = useSelector((state: RootState) => {
        return stepRun ? stepData.platform : selectAppPlatform(state, appId);
    });
    const appStoreType = useSelector((state: RootState) => {
        return stepRun ? stepData.appStoreType : selectAppStoreType(state, appId);
    });
    const enablePlayReleaseLifecycle = useSelector(
        (state: RootState) => !!selectOrg(state, orgId)?.enablePlayReleaseLifecycle,
    );

    const appStoreData = stepData.appStoreData;
    // Legacy release step payload has no top-level phasedRelease
    const phasedRelease =
        stepData.phasedRelease ?? (appStoreData && 'phasedRelease' in appStoreData ? appStoreData.phasedRelease : null);

    const rolloutStartedAt = phasedRelease?.startDate ?? stepData.releasedAt;

    const [releaseConfirmationModalVisible, setReleaseConfirmationModalVisible] = React.useState(false);

    const automationRuns = stepRun ? stepRun.automationRuns : releaseStep.automationRuns;

    const isAutoTagEnabled = useSelector((state: RootState) =>
        selectIsAutomationEnabledForReleaseType(
            state,
            appId,
            getAutomationIdByType(automationRuns, AutomationType.AutoTagReleases),
            release.type,
        ),
    );

    const autoStagedRolloutAutomationId =
        stepRun && stepData.autoStagedRolloutAutomationId ?
            stepData.autoStagedRolloutAutomationId
        :   getAutomationIdByType(automationRuns, AutomationType.AutoStagedRollout);

    const autoStagedRolloutAutomation = useSelector((state: RootState) =>
        selectAutomationById(state, appId, autoStagedRolloutAutomationId),
    ) as AutomationAutoStagedRollout;

    const autoStagedRolloutEnabled = autoStagedRolloutAutomation?.enabled;
    const autoStagedRolloutStartStrategy = autoStagedRolloutAutomation?.data?.autoStagedRolloutStartStrategy;

    const isCustomStagedRolloutEnabled = autoStagedRolloutEnabled === true;
    // Only the Manual and AutoFromForwardedEmail strategies need the user to start the
    // rollout; the Auto strategy starts it automatically at submit time. This matches the
    // BE's NeedsManualAutoStagedRolloutStart check in step_store_release.go.
    const strategyRequiresManualStart =
        autoStagedRolloutStartStrategy === AndroidAutoRolloutStartStrategy.Manual ||
        autoStagedRolloutStartStrategy === AndroidAutoRolloutStartStrategy.AutoFromForwardedEmail;
    const needsManualRolloutStart = isCustomStagedRolloutEnabled && strategyRequiresManualStart && !rolloutStartedAt;

    const appleAppStoreData = appStoreData as AppleAppStoreData;
    const googlePlayStoreData = appStoreData as GooglePlayStoreData;
    const amazonAppStoreData = appStoreData as AmazonAppStoreData;
    const huaweiAppStoreData = appStoreData as HuaweiAppStoreData;
    const samsungAppStoreData = appStoreData as SamsungAppStoreData;

    const appStoreIntegrationId = useSelector((state: RootState) => selectAppStoreIntegration(state, appId)?.id);

    const isAppleAppStore = getIsAppleAppStore(appStoreType);
    const isSamsungAppStore = getIsSamsungGalaxyStore(appStoreType);

    const stepStatus = stepRun ? stepRun.status : releaseStep.status;
    const storeUrl = stepData.storeUrl;
    const appStoreUrl = stepData.appStoreUrl;

    const isReleased = isAppReleased(
        appStoreType,
        appleAppStoreData?.versionReviewSubmissionItem?.state,
        googlePlayStoreData?.state,
        amazonAppStoreData?.state,
        huaweiAppStoreData?.state,
        samsungAppStoreData?.state,
    );

    const willBeReleasedByRunway = useSelector((state: RootState) =>
        selectReleaseIsRunwayAutomated(state, appId, releaseId),
    );

    let autoReleaseAutomation = useSelector((state: RootState) =>
        selectAutomationById(state, appId, getAutomationIdByType(automationRuns, AutomationType.AutoRelease)),
    ) as AutomationAutoRelease;

    if (!willBeReleasedByRunway) {
        autoReleaseAutomation = undefined;
    }

    const autoReleaseStrategy = autoReleaseAutomation?.data?.strategy;

    const autoSubmitAutomationRun = useSelector((state: RootState) =>
        selectAutoSubmitStepAutomationRun(state, releaseId),
    );
    const autoSubmitAutomation = useSelector((state: RootState) =>
        selectAutomationById(state, appId, autoSubmitAutomationRun?.id),
    ) as AutomationAutoSubmitRelease;

    const willAutoPromotePreviousRelease =
        autoSubmitAutomation?.enabled && autoSubmitAutomation?.data?.shouldReleasePreviousSubmissionToAllUsersFirst;

    const isReleasedByRunway = useSelector((state: RootState) =>
        selectReleaseSuccessfullyRunwayAutomated(state, appId, releaseId),
    );
    const isRollback = useSelector((state: RootState) => selectIsReleaseRollback(state, releaseId));

    const isPhased = isPhasedRelease(platform, appleAppStoreData, googlePlayStoreData);
    const isPhaseReleasePausedByRunway = isPhasedReleasePausedByRunway(appStoreType, appleAppStoreData?.phasedRelease);
    const isPhaseReleaseAcceleratedByRunway = isPhasedReleaseAcceleratedByRunway(phasedRelease);
    const phasedReleaseComplete = isPhased && isReleased ? isPhasedReleaseComplete(phasedRelease) : isReleased;

    const tagData = stepData.tagData;

    const [
        releaseAppleVersionStepRun,
        releaseAppleVersionIsLoadingStepRun,
        releaseAppleVersionResponseTypeStepRun,
        releaseAppleVersionErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.ReleaseAppleVersion, stepRunComponentProps);

    const submitAppleVersionIsLoadingReleaseStep = useIsLoading(ReleaseUpdatable.ReleaseAppleVersion, releaseId);
    const submitAppleVersionErrorReleaseStep = useError(ReleaseUpdatable.ReleaseAppleVersion, releaseId);
    const submitAppleVersionSuccessReleaseStep = useUpdated(ReleaseUpdatable.ReleaseAppleVersion, releaseId);

    const submitAppleVersionIsLoading =
        stepRunComponentProps ? releaseAppleVersionIsLoadingStepRun : submitAppleVersionIsLoadingReleaseStep;
    const submitAppleVersionError =
        stepRunComponentProps ? releaseAppleVersionErrorStepRun : submitAppleVersionErrorReleaseStep;
    const submitAppleVersionSuccess =
        stepRunComponentProps ?
            releaseAppleVersionResponseTypeStepRun === RunwayResponseType.Success
        :   submitAppleVersionSuccessReleaseStep;

    React.useEffect(() => {
        if (submitAppleVersionSuccess) {
            setReleaseConfirmationModalVisible(false);
            globalMessage.success(`Successfully released your ${displayableVersion} update!`);
        }
    }, [submitAppleVersionSuccess]);

    React.useEffect(() => {
        if (submitAppleVersionError) {
            globalMessage.error(submitAppleVersionError);
        }
    }, [submitAppleVersionError]);

    const expectedReleaseTagName = tagData.expectedReleaseTagName;
    const releaseTag = tagData.releaseTag;
    const releaseTagState = tagData.releaseTagState;
    const codeDiffUrl = tagData.codeDiffUrl;

    const hasVCSConnected = useSelector((state: RootState) => selectHasVCSIntegrationConnected(state, appId));
    const latestCompletedVersion = useSelector((state: RootState) => selectLatestVersion(state, appId));
    const shouldForceShowAutomatedReleaseTasks =
        release.status === ReleaseStatus.Completed &&
        stepData.isReleaseTagged === false &&
        hasVCSConnected &&
        latestCompletedVersion === release.version;

    const dispatch = useDispatch();

    const releaseAppleVersion = (stepRunRequest: ReleaseAppleVersionRequest) => {
        if (stepRunComponentProps) {
            releaseAppleVersionStepRun(stepRunRequest);
        } else {
            const request: ReleaseAppleVersionRequestReleaseStep = {
                orgId,
                appId,
                releaseId,
                releasePreviousSubmissionToAllUsersFirst: stepRunRequest.releasePreviousSubmissionToAllUsersFirst,
            };
            dispatch(tryUpdateRelease(ReleaseUpdatable.ReleaseAppleVersion, request, request.releaseId));
        }
    };

    const stabilityIntegrations = useSelector((state: RootState) => selectStabilityIntegrations(state, appId));
    const stabilityIntegrationIcons = stabilityIntegrations
        .map((integration) => iconAndAltForIntegrationType(integration.id))
        .filter(([icon]) => !!icon);

    const currentlySelectedAppStoreBuilds = stepData.appStoreSelectedBuilds ?? [];
    const shouldShowLastUploadedBuildComponent = shouldShowLastUploadedBuilds(
        currentlySelectedAppStoreBuilds,
        appStoreType,
    );

    const buildsWithStabilityInfo = compact(
        currentlySelectedAppStoreBuilds.map((build) => {
            if (getStabilityInfos(build).length > 0) {
                return build;
            } else {
                return null;
            }
        }),
    );

    const isRunwayPausedRelease = isPhasedReleasePausedByRunway(appStoreType, phasedRelease);

    const phasedReleaseHasBeenSupersededByNewRelease = stepData.phasedReleaseHasBeenSupersededByNewRelease;

    const rolloutStepRunId = useSelector((state: RootState) =>
        selectRolloutStepRunIdForDestinationStepRun(state, releaseId, stepRunId),
    );
    const deferRolloutDetailToRolloutStep =
        !!rolloutStepRunId &&
        isReleased &&
        (isPhased || needsManualRolloutStart || phasedReleaseHasBeenSupersededByNewRelease);
    const [rolloutSentenceLeadIn, rolloutSentenceTrailer] =
        needsManualRolloutStart ? ['You can start ', ' at any time.']
        : isCustomStagedRolloutEnabled ? ['Runway is managing ', '.']
        : ['You can update ', ' at any time.'];

    const [showEditModal, setShowEditModal] = React.useState(false);

    const isInEmptyState = useIsInEmptyState(releaseId, appId, releaseStepType);

    const releaseSummary = useSelector((state: RootState) => {
        const release = selectRelease(state, releaseId);
        if (!release.releaseSummaryMessage) {
            return 'Release summary not available.';
        } else {
            return release.releaseSummaryMessage.message;
        }
    });

    const relevantReviewSubmission = appleAppStoreData?.relevantReviewSubmission;

    const filteredReviewSubmissionItems = relevantReviewSubmission?.reviewSubmissionItems?.filter(
        (item) => item.state !== AppStoreReviewSubmissionItemState.Removed,
    );

    const autoPromoteStablePhasedReleasesAutomationId = getAutomationIdByType(
        automationRuns,
        AutomationType.AutoPromoteStablePhasedReleases,
    );

    const autoHaltUnstablePhasedReleasesAutomationId = getAutomationIdByType(
        automationRuns,
        AutomationType.AutoHaltUnstablePhasedReleases,
    );

    const {
        daysToPercent,
        hasPhasedReleaseState,
        isPhasedRollout: isAutomatedPhasedRollout,
        rolloutComplete,
    } = usePhasedReleaseState({
        appId,
        appStoreType,
        phasedRelease,
        autoStagedRolloutAutomationId,
        rolloutStartedAt,
        daysToPercentSchedule: stepData.autoStagedRolloutDaysToPercent,
        isAutomatedPhasedRollout: stepData.isAutomatedPhasedRollout,
    });

    const rolloutCollapsibleItems: AppStoreCollapsibleItem[] =
        (isPhased && isReleased) || rolloutComplete || isInEmptyState ?
            [
                {
                    header: isAppleAppStore ? 'Phased release' : 'Staged rollout',
                    content: (
                        <EmptyStateWrapper
                            releaseStepType={releaseStepType}
                            appId={appId}
                            featureSections={[FeatureSection.ReleasePhasedReleaseInfo]}
                            releaseId={releaseId}
                            shouldForceDisable={hasPhasedReleaseState}
                            shouldCenterContent
                        >
                            {() => (
                                <PhasedReleaseCard
                                    stepRunComponentProps={stepRunComponentProps}
                                    orgId={orgId}
                                    appId={appId}
                                    releaseId={releaseId}
                                    platform={platform}
                                    appStoreType={appStoreType}
                                    appStoreIntegrationId={appStoreIntegrationId}
                                    phasedRelease={phasedRelease}
                                    rolloutStartedAt={rolloutStartedAt}
                                    isAutomatedPhasedRollout={isAutomatedPhasedRollout}
                                    daysToPercentSchedule={daysToPercent}
                                    autoStagedRolloutAutomationId={autoStagedRolloutAutomationId}
                                    autoPromoteStablePhasedReleasesAutomationId={
                                        autoPromoteStablePhasedReleasesAutomationId
                                    }
                                    autoHaltUnstablePhasedReleasesAutomationId={
                                        autoHaltUnstablePhasedReleasesAutomationId
                                    }
                                />
                            )}
                        </EmptyStateWrapper>
                    ),
                    extra: (isExpanded) => {
                        return (
                            <EmptyStateWrapper
                                releaseStepType={releaseStepType}
                                appId={appId}
                                featureSections={[FeatureSection.ReleasePhasedReleaseInfo]}
                                releaseId={releaseId}
                                shouldForceDisable={hasPhasedReleaseState}
                                shouldCenterContent
                            >
                                {() =>
                                    // The rollout CTAs live inside the card itself, so the
                                    // collapsed chart is all this slot renders now.
                                    isExpanded ? null : (
                                        <PhasedReleaseInlineChart
                                            wasAccelerated={wasAccelerated}
                                            isAutomatedPhasedRollout={isAutomatedPhasedRollout}
                                            wasHalted={wasHalted}
                                            phasedRelease={phasedRelease}
                                            daysToPercent={daysToPercent}
                                        />
                                    )
                                }
                            </EmptyStateWrapper>
                        );
                    },
                },
            ]
        :   [];

    const commonComponents: React.ReactNode = (
        <AppStoreItemsCollapse
            header={
                shouldShowLastUploadedBuildComponent ?
                    <LastUploadedAppStoreBuildModuleEmbedded
                        appStoreType={appStoreType}
                        appStoreBuild={stepData.lastSuccessfullyUploadedBuild}
                        releaseStepType={releaseStepType}
                        releaseId={releaseId}
                        appId={appId}
                        orgId={orgId}
                    />
                :   <EmptyStateWrapper
                        releaseStepType={releaseStepType}
                        appId={appId}
                        featureSections={[FeatureSection.ReleaseReleasedBuild]}
                        releaseId={releaseId}
                        shouldForceDisable={currentlySelectedAppStoreBuilds.length > 0}
                    >
                        <div>
                            <SelectedAppStoreBuildModuleEmbedded
                                stepRunComponentProps={stepRunComponentProps}
                                orgId={orgId}
                                appId={appId}
                                releaseId={releaseId}
                                releaseStepType={releaseStepType}
                                platform={platform}
                                appStoreType={appStoreType}
                                appStoreState={
                                    appleAppStoreData?.versionReviewSubmissionItem?.state ??
                                    googlePlayStoreData?.state ??
                                    amazonAppStoreData?.state ??
                                    huaweiAppStoreData?.state ??
                                    samsungAppStoreData?.state
                                }
                                reviewSubmissionState={appleAppStoreData?.relevantReviewSubmission?.state}
                                showChangeSelectedBuildCTA={false}
                                appStoreAvailableBuilds={stepData.appStoreAvailableBuilds}
                                appStoreSelectedBuilds={currentlySelectedAppStoreBuilds}
                                additionalAppStoreSelectedBuilds={stepData.additionalAppStoreSelectedBuilds}
                                retainedBuildsManuallySet={stepData.retainedBuildsManuallySet}
                                latestSuccessfulProdBuild={stepData.latestSuccessfulProdBuild}
                                latestSuccessfulBuild={stepData.latestSuccessfulBuild}
                                selectedStagingBuild={stepData.selectedStagingBuild}
                                latestPassingRegressionTestBuild={stepData.latestPassingRegressionTestBuild}
                                buildMatchingWarnings={stepData?.buildMatchingWarnings}
                                willAutoPromotePreviousRelease={willAutoPromotePreviousRelease}
                            />
                        </div>
                    </EmptyStateWrapper>
            }
            collapsibleItems={[
                ...(!isRollback ?
                    [
                        {
                            header: 'Release summary',
                            content: (
                                <ReleaseSummaryMessageComponentBase
                                    orgId={orgId}
                                    appId={appId}
                                    releaseId={releaseId}
                                    releaseStepType={releaseStepType}
                                    version={releaseVersion}
                                    showEditModal={showEditModal}
                                    setShowEditModal={setShowEditModal}
                                    backgroundColor={runwayColors.white}
                                />
                            ),
                            extra: (isExpanded) => {
                                return isExpanded ?
                                        <PermissionedActionButton
                                            releaseId={releaseId}
                                            actionType={RunwayUserAction.UAEditReleaseSummaryMessage}
                                            icon={<EditOutlined style={{ color: runwayColors.info }} />}
                                            onClick={() => setShowEditModal(true)}
                                        >
                                            Edit release summary
                                        </PermissionedActionButton>
                                    :   <Text
                                            ellipsis
                                            style={{
                                                fontWeight: 400,
                                                color: antGrey.grey7,
                                                fontStyle: 'italic',
                                                maxWidth: 450,
                                            }}
                                        >
                                            {releaseSummary}
                                        </Text>;
                            },
                        },
                    ]
                :   []),
                ...(buildsWithStabilityInfo.length > 0 ?
                    [
                        {
                            header: (
                                <Space>
                                    {stabilityIntegrationIcons.length > 0 && (
                                        <Space size={4}>
                                            {stabilityIntegrationIcons.map(([icon, alt]) => (
                                                <img key={alt} src={icon} alt={alt} width={20} height={20} />
                                            ))}
                                        </Space>
                                    )}
                                    <Text style={{ ...releaseStepModuleTitleStyle, alignSelf: 'center' }}>
                                        Stability
                                    </Text>
                                </Space>
                            ),
                            content: (
                                <StabilityMetricsContainerBase
                                    appId={appId}
                                    backgroundColor={runwayColors.white}
                                    isRunwayAutoPaused={isRunwayPausedRelease}
                                    builds={buildsWithStabilityInfo}
                                />
                            ),
                            extra: (isExpanded) => {
                                const count = buildsWithStabilityInfo.length;

                                if (count !== 1) {
                                    // Only show something if there is a single build, we don't support showing multiple builds
                                    return null;
                                }

                                const firstBuild = buildsWithStabilityInfo[0];
                                const stabilityInfos = getStabilityInfos(firstBuild);

                                if (stabilityInfos.length === 0) {
                                    return null;
                                }

                                if (isExpanded) {
                                    const withUrls = stabilityInfos.filter((info) => !!info.url);
                                    if (withUrls.length === 0) {
                                        return null;
                                    }
                                    return (
                                        <Space size={8} wrap>
                                            {withUrls.map((info) => (
                                                <ViewInStabilityPlatformButton
                                                    key={`${info.integrationId}-${info.buildIdentifier ?? ''}`}
                                                    stabilityInfo={info}
                                                />
                                            ))}
                                        </Space>
                                    );
                                }

                                return (
                                    <Space direction="vertical" size={4}>
                                        {stabilityInfos.map((info) => {
                                            const [icon, alt] = iconAndAltForIntegrationType(info.integrationId);
                                            return (
                                                <Space
                                                    key={`${info.integrationId}-${info.buildIdentifier ?? ''}`}
                                                    size={6}
                                                    align="center"
                                                >
                                                    {icon && <img src={icon} alt={alt} width={14} height={14} />}
                                                    <StabilityMetricsInlinePercentages stabilityInfo={info} />
                                                </Space>
                                            );
                                        })}
                                    </Space>
                                );
                            },
                        },
                    ]
                :   []),

                {
                    header: (
                        <Space>
                            <AutomatedByRunwayIcon />
                            <Text style={{ ...releaseStepModuleTitleStyle, alignSelf: 'center' }}>
                                Automated release tasks
                            </Text>
                        </Space>
                    ),
                    content: (
                        <EmptyStateWrapper
                            releaseStepType={releaseStepType}
                            appId={appId}
                            featureSections={[FeatureSection.ReleaseAutomatedReleaseTasks]}
                            releaseId={releaseId}
                            shouldForceDisable={shouldForceShowAutomatedReleaseTasks}
                        >
                            <div style={{ padding: 6 }}>
                                <AutomatedTakeoffTasksBase
                                    stepRunComponentProps={stepRunComponentProps}
                                    orgId={orgId}
                                    predictedTagName={expectedReleaseTagName}
                                    releaseTagState={releaseTagState}
                                    releaseTag={releaseTag}
                                    appId={appId}
                                    releaseId={releaseId}
                                    userId={userId}
                                    codeDiffUrl={codeDiffUrl}
                                    releaseVersion={releaseVersion}
                                    isAutoTagEnabled={isAutoTagEnabled}
                                />
                            </div>
                        </EmptyStateWrapper>
                    ),
                    extra: (isExpanded) => {
                        return (
                            !isExpanded && (
                                <CreatedTagInfo releaseTag={releaseTag} predictedTagName={expectedReleaseTagName} />
                            )
                        );
                    },
                },
            ]}
        />
    );

    const wasAccelerated =
        phasedRelease?.isManuallyAcceleratedPhasedRollout || phasedRelease?.isRunwayAcceleratedRollout;
    const wasHalted = isPhasedReleasePaused(phasedRelease?.state);

    const isReadyForAppleRelease =
        isAppleAppStore &&
        appleAppStoreData &&
        appleAppIsReadyToRelease(appleAppStoreData.versionReviewSubmissionItem?.state);

    const isReadyForSamsungRelease =
        isSamsungAppStore && samsungAppStoreData?.state === SamsungAppStoreState.ReadyForChange;

    const canBeReleased = isReadyForAppleRelease || isReadyForSamsungRelease;

    return (
        <Layout>
            <Content
                style={{
                    ...styles.innerContent,
                    ...styles.contentWithFooterStyle,
                    paddingTop: 0,
                }}
            >
                <div style={{ display: 'flex', marginBottom: 30, justifyContent: 'space-between' }}>
                    <EmptyStateWrapper
                        releaseStepType={releaseStepType}
                        appId={appId}
                        releaseId={releaseId}
                        featureSections={[]}
                        shouldForceDisable
                    >
                        {(isReleaseFromScraper) => {
                            if (deferRolloutDetailToRolloutStep && !isReleaseFromScraper) {
                                return (
                                    <Space direction="vertical" size={4} style={{ paddingTop: 28, marginRight: 15 }}>
                                        <Text
                                            style={{
                                                ...styles.stepContentStatusText,
                                                color: runwayColors.statusGreen,
                                            }}
                                        >
                                            Release preparation complete
                                        </Text>
                                        <Text type="secondary">
                                            Your app update is live in the{' '}
                                            {displayStoreNameForAppStoreType(appStoreType)}. {rolloutSentenceLeadIn}
                                            <Link
                                                to={createStepRunRoute(
                                                    orgId,
                                                    appId,
                                                    release.releaseId,
                                                    rolloutStepRunId,
                                                )}
                                            >
                                                your rollout
                                            </Link>
                                            {rolloutSentenceTrailer}
                                        </Text>
                                    </Space>
                                );
                            }

                            return (
                                <Space direction="vertical" size={4} style={{ paddingTop: 28, marginRight: 15 }}>
                                    <Text
                                        style={{
                                            ...styles.stepContentStatusText,
                                            color: titleColor(
                                                stepStatus,
                                                isReleased,
                                                isPhased,
                                                phasedReleaseComplete,
                                                isReleaseFromScraper,
                                            ),
                                        }}
                                    >
                                        {titleForState(
                                            appStoreType,
                                            isReleased,
                                            isPhased,
                                            stepStatus,
                                            isPhaseReleasePausedByRunway,
                                            isReleaseFromScraper,
                                            appleAppStoreData,
                                            googlePlayStoreData,
                                            needsManualRolloutStart,
                                            phasedReleaseHasBeenSupersededByNewRelease,
                                            enablePlayReleaseLifecycle,
                                        )}
                                    </Text>
                                    <Text type="secondary">
                                        {showsReleaseDelay(stepData, isReleased, stepStatus) ?
                                            <ReleaseDelayDescription
                                                orgId={orgId}
                                                appId={appId}
                                                releaseDelay={stepData.releaseDelay}
                                                plannedReleaseDelayMinutes={stepData.plannedReleaseDelayMinutes}
                                                isSubmission={false}
                                                automationId={getAutomationIdByType(
                                                    automationRuns,
                                                    AutomationType.AutoRelease,
                                                )}
                                            />
                                        :   descriptionForState(
                                                platform,
                                                appStoreType,
                                                isReleased,
                                                isReleasedByRunway,
                                                autoReleaseStrategy,
                                                isPhased,
                                                stepStatus,
                                                isPhaseReleasePausedByRunway,
                                                isPhaseReleaseAcceleratedByRunway,
                                                isCustomStagedRolloutEnabled,
                                                isReleaseFromScraper,
                                                phasedRelease,
                                                appleAppStoreData,
                                                googlePlayStoreData,
                                                amazonAppStoreData,
                                                needsManualRolloutStart,
                                                phasedReleaseHasBeenSupersededByNewRelease,
                                                enablePlayReleaseLifecycle,
                                            )
                                        }
                                    </Text>
                                </Space>
                            );
                        }}
                    </EmptyStateWrapper>
                    <ReleaseEventsTimelineSection
                        orgId={orgId}
                        appId={appId}
                        releaseId={releaseId}
                        stepRunId={stepRunComponentProps?.stepRunId}
                        context={ReleaseTimelineSectionContext.ReleaseStep}
                        releaseStepType={releaseStepType}
                    />
                </div>

                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 12,
                        width: '100%',
                    }}
                >
                    {rolloutCollapsibleItems.length > 0 && (
                        <AppStoreItemsCollapse collapsibleItems={rolloutCollapsibleItems} />
                    )}
                    {commonComponents}
                    {filteredReviewSubmissionItems?.map((reviewSubmissionItem) =>
                        renderReviewsSubmissionItemCard({
                            reviewSubmissionItemId: reviewSubmissionItem.id,
                            item: reviewSubmissionItem.item,
                            type: reviewSubmissionItem.type,
                            stepRunComponentProps,
                        }),
                    )}
                </div>
            </Content>
            <Footer style={styles.footerStyle}>
                <Space>
                    <TrackedButton
                        eventName={AnalyticEvents.Event.ClickAppStoreConsoleButton}
                        orgId={orgId}
                        appId={appId}
                        releaseId={releaseId}
                        releaseStepType={releaseStepType}
                        context={AnalyticEvents.Context.ReleaseStep}
                        href={storeUrl}
                        icon={<ArrowRightOutlined style={{ color: runwayColors.info }} />}
                        target="_blank"
                    >
                        {appStoreButtonText(appStoreType)}
                    </TrackedButton>
                    {canBeReleased && !submitAppleVersionSuccess && (
                        <PermissionedActionButton
                            releaseId={releaseId}
                            actionType={RunwayUserAction.UAReleaseAppUpdate}
                            icon={iconForPlatform(platform)}
                            type="primary"
                            onClick={() => setReleaseConfirmationModalVisible(true)}
                            loading={submitAppleVersionIsLoading}
                        >
                            Release {displayableVersion} update
                        </PermissionedActionButton>
                    )}
                    {isReleased && (
                        <Button icon={iconForPlatform(platform, runwayColors.info)} href={appStoreUrl} target="_blank">
                            View {displayableVersion} in the {displayStoreNameForAppStoreType(appStoreType)}
                        </Button>
                    )}
                </Space>
            </Footer>
            <AppStoreReleaseConfirmationModal
                open={releaseConfirmationModalVisible}
                orgId={orgId}
                appId={appId}
                releaseId={releaseId}
                stepRunId={stepRunComponentProps?.stepRunId}
                confirmLoading={submitAppleVersionIsLoading}
                statusMessage={submitAppleVersionIsLoading ? stepData.unconfirmedReleaseCheckMessage : undefined}
                onConfirm={releaseAppleVersion}
                onClose={() => setReleaseConfirmationModalVisible(false)}
                appStoreType={appStoreType}
                displayableVersion={displayableVersion}
                lastCompletedRelease={stepData.lastCompletedRelease}
                releaseDelay={stepData.releaseDelay}
                plannedReleaseDelayMinutes={stepData.plannedReleaseDelayMinutes}
            />
        </Layout>
    );
};

const appleTitleFromAppStoreData = (
    appleStoreData: AppleAppStoreData,
    isReleased: boolean,
    isPhased: boolean,
    isPhasedReleasePausedByRunway: boolean,
    phasedReleaseHasBeenSupersededByNewRelease?: boolean,
) => {
    if (isReleased) {
        if (isPhased) {
            if (phasedReleaseHasBeenSupersededByNewRelease) {
                return 'Superseded';
            }
            switch (appleStoreData.phasedRelease?.state) {
                case AppleAppStorePhasedReleaseState.Active:
                    return 'Phased release in progress';
                case AppleAppStorePhasedReleaseState.Paused:
                    return isPhasedReleasePausedByRunway ? 'Phased release auto-paused' : 'Phased release paused';
                case AppleAppStorePhasedReleaseState.Complete:
                    return 'Phased release complete';
                case AppleAppStorePhasedReleaseState.Inactive:
                    return 'Phased release inactive';
            }
        }

        return 'Released';
    } else if (appleStoreData?.versionReviewSubmissionItem?.state === AppleAppStoreState.ProcessingForAppStore) {
        return 'Processing for release';
    } else if (appleStoreData?.versionReviewSubmissionItem?.state === AppleAppStoreState.PendingAppleRelease) {
        return 'Pending Apple release';
    } else {
        return 'Ready to release';
    }
};

const googleTitleFromAppStoreData = (
    isReleased: boolean,
    isPhased: boolean,
    state: GooglePlayStoreState,
    isPhasedReleasePausedByRunway: boolean,
    needsManualRolloutStart: boolean,
    enablePlayReleaseLifecycle?: boolean,
    lifecycleState?: GooglePlayReleaseLifecycleState | '',
) => {
    // For flagged orgs in managed publishing, lifecycle = _APPROVED_NOT_PUBLISHED means
    // Google has approved review but the developer still has to publish via Play Console.
    // The rollout-axis can already report InProgress at this point, so the legacy
    // "Ready to start staged rollout" / "Staged rollout in progress" titles are misleading
    // — we want "Ready to release" to match iOS's PendingDeveloperRelease vocabulary.
    if (enablePlayReleaseLifecycle && lifecycleState === GooglePlayReleaseLifecycleState.ApprovedNotPublished) {
        return 'Ready to release';
    }

    if (isReleased) {
        if (isPhased) {
            switch (state) {
                case GooglePlayStoreState.InProgress:
                    return needsManualRolloutStart ? 'Ready to start staged rollout' : 'Staged rollout in progress';
                case GooglePlayStoreState.Halted:
                    return isPhasedReleasePausedByRunway ? 'Staged rollout auto-halted' : 'Staged rollout halted';
                case GooglePlayStoreState.Completed:
                    return 'Staged rollout complete';
                default:
                    return '';
            }
        }

        return 'Released';
    } else {
        return 'Ready to release';
    }
};

const amazonTitleFromAppStoreData = (isReleased: boolean) => {
    return isReleased ? 'Released' : 'Ready to release';
};

const huaweiTitleFromAppStoreData = (isReleased: boolean) => {
    return isReleased ? 'Released' : 'Ready to release';
};

const samsungTitleFromAppStoreData = (isReleased: boolean) => {
    return isReleased ? 'Released' : 'Ready to release';
};

const huaweiDescriptionFromAppStoreData = (isReleased: boolean, isReleasedByRunway: boolean) => {
    if (isReleased) {
        return isReleasedByRunway ?
                'Runway successfully released your app update to the Huawei AppGallery.'
            :   'Your app update was successfully released to the Huawei AppGallery.';
    } else {
        return 'Your app will be made available for download once it has been reviewed.';
    }
};

const samsungDescriptionFromAppStoreData = (isReleased: boolean, isReleasedByRunway: boolean) => {
    if (isReleased) {
        return isReleasedByRunway ?
                'Runway successfully released your app update to the Samsung Galaxy Store.'
            :   'Your app update was successfully released to the Samsung Galaxy Store.';
    } else {
        return 'Your app will be made available for download once it has been reviewed.';
    }
};

const amazonDescriptionFromAppStoreData = (
    isReleased: boolean,
    isReleasedByRunway: boolean,
    amazonStoreData?: AmazonAppStoreData,
) => {
    if (isReleased) {
        if (amazonStoreData?.state === AmazonAppStoreState.Completed) {
            return isReleasedByRunway ?
                    'Runway successfully released your app update to the Amazon Appstore.'
                :   'Your app update was successfully released to the Amazon Appstore.';
        } else {
            return 'Your app update is currently being processed for release to the Amazon Appstore.';
        }
    } else {
        return 'Your app will be made available for download once it has been reviewed.';
    }
};

const googleDescriptionFromAppStoreData = (
    isReleased: boolean,
    isReleasedByRunway: boolean,
    isPhased: boolean,
    isPhasedReleasePausedByRunway: boolean,
    isPhasedReleaseAcceleratedByRunway: boolean,
    isRunwayAutomatedStagedRollout: boolean,
    needsManualRolloutStart: boolean,
    googleStoreData?: GooglePlayStoreData,
    enablePlayReleaseLifecycle?: boolean,
) => {
    if (enablePlayReleaseLifecycle) {
        switch (googleStoreData?.releaseLifecycleState) {
            case GooglePlayReleaseLifecycleState.Draft:
            case GooglePlayReleaseLifecycleState.NotSentForReview:
                return 'This release has not been sent to Google Play for review yet.';
            case GooglePlayReleaseLifecycleState.InReview:
                return 'Your app update is in review by Google Play.';
            case GooglePlayReleaseLifecycleState.ApprovedNotPublished:
                return "Because this app uses Google Play's managed publishing, this release can only be published from the Google Play Console.";
            case GooglePlayReleaseLifecycleState.NotApproved:
                return 'Your release was not approved by Google Play.';
        }
    }

    if (isReleased) {
        if (isPhased && googleStoreData?.phasedRelease) {
            const storeText = displayStoreNameForAppStoreType(AppStoreType.Google);

            switch (googleStoreData?.state) {
                case GooglePlayStoreState.InProgress: {
                    if (needsManualRolloutStart) {
                        return 'Start your staged rollout to begin the automated rollout sequence.';
                    } else if (isRunwayAutomatedStagedRollout) {
                        return 'Your automated staged rollout has started. Runway is managing your rollout.';
                    } else {
                        return isReleasedByRunway ?
                                `Runway successfully released your app update to the ${storeText} via staged rollout.`
                            :   `Your app update was successfully released to the ${storeText} via staged rollout.`;
                    }
                }

                case GooglePlayStoreState.Halted:
                    return isPhasedReleasePausedByRunway ?
                            'Runway automatically paused your phased release due to a crash rate exceeding your set threshold.'
                        :   `Your staged rollout to the ${storeText} has been halted.`;
                case GooglePlayStoreState.Completed:
                    return isPhasedReleaseAcceleratedByRunway ?
                            `Successfully rolled out your app update to 100% of users. Runway accelerated your staged rollout to 100% when it reached your target adoption rate while staying within your target stability threshold.`
                        :   `Your staged rollout is complete and has been released to all users.`;
                default:
                    return '';
            }
        } else {
            const storeText = displayStoreNameForAppStoreType(AppStoreType.Google);

            switch (googleStoreData?.state) {
                case GooglePlayStoreState.InProgress:
                case GooglePlayStoreState.Halted:
                    // shouldn't be possible if release is not phased
                    return '';
                case GooglePlayStoreState.Completed:
                    return isReleasedByRunway ?
                            `Runway successfully released your app update to the ${storeText}.`
                        :   `Your app update was successfully released to the ${storeText}.`;
                case GooglePlayStoreState.StatusUnspecified:
                    return 'Unspecified update status.';
                default:
                    return '';
            }
        }
    } else {
        return 'Your app will be made available for download once it has been reviewed and published.';
    }
};

const appleDescriptionFromAppStoreData = (
    isReleased: boolean,
    isReleasedByRunway: boolean,
    autoReleaseStrategy: AutoReleaseStrategy | undefined,
    isPhased: boolean,
    releaseStepStatus: ReleaseStepStatus,
    isPhasedReleasePausedByRunway: boolean,
    isPhasedReleaseAcceleratedByRunway: boolean,
    appleStoreData?: AppleAppStoreData,
    phasedReleaseHasBeenSupersededByNewRelease?: boolean,
) => {
    if (isReleased) {
        if (isPhased && appleStoreData?.phasedRelease) {
            if (phasedReleaseHasBeenSupersededByNewRelease) {
                return 'Your phased release was superseded by a newer app version.';
            }

            const phasedReleaseState = appleStoreData.phasedRelease?.state as AppleAppStorePhasedReleaseState;

            switch (phasedReleaseState) {
                case AppleAppStorePhasedReleaseState.Inactive:
                    // released with release state inactive is probably not possible?
                    return 'This is a phased release. Your app will be released to users over a period of 7 days.';
                case AppleAppStorePhasedReleaseState.Active:
                    return isReleasedByRunway ?
                            `Runway successfully released your app update. Your 7-day phased release in the App Store is active.`
                        :   'Your 7-day phased release in the App Store is active.';
                case AppleAppStorePhasedReleaseState.Paused:
                    return isPhasedReleasePausedByRunway ?
                            'Runway automatically paused your phased release due to a crash rate exceeding your set threshold.'
                        :   'Your 7-day phased release in the App Store has been paused.';
                case AppleAppStorePhasedReleaseState.Complete:
                    return (
                        isPhasedReleaseAcceleratedByRunway ?
                            `Successfully released your app update to 100% of users. Runway accelerated your phased release to 100% after ${
                                appleStoreData?.phasedRelease?.dayNumber ?? 'unknown'
                            } days when it reached your target adoption rate while staying within your target stability threshold.`
                        : isReleasedByRunway ?
                            `Runway automatically released your update and your phased release is now complete.`
                        :   'Your phased release is complete.'
                    );
            }
        } else {
            switch (appleStoreData?.versionReviewSubmissionItem?.releaseType) {
                case AppleAppStoreReleaseType.AfterApproval:
                    return 'This update is available to all users.';
                case AppleAppStoreReleaseType.Manual:
                    return isReleasedByRunway ?
                            `Runway has successfully released your app update to all users.`
                        :   'You have successfully released your app update to all users.';
                case AppleAppStoreReleaseType.Scheduled:
                    if (appleStoreData.versionReviewSubmissionItem?.earliestReleaseDate) {
                        const releaseDate = dayjs(
                            appleStoreData.versionReviewSubmissionItem?.earliestReleaseDate,
                        ).format(DatetimeFormat.DayMonthDateYear);

                        return isReleasedByRunway ?
                                `Runway successfully released your app update on ${releaseDate}`
                            :   `Your app update was released on ${releaseDate}`;
                    } else {
                        return isReleasedByRunway ?
                                `Runway successfully released your app update on the scheduled date.`
                            :   'Your app update was released on the scheduled date.';
                    }
                default:
                    return '';
            }
        }
    } else {
        switch (releaseStepStatus) {
            case ReleaseStepStatus.Inactive:
                switch (appleStoreData?.versionReviewSubmissionItem?.releaseType) {
                    case AppleAppStoreReleaseType.AfterApproval:
                        return 'Your app will be made available for download to all users automatically once it has been approved by the App Store Review team.';
                    case AppleAppStoreReleaseType.Manual:
                        return autoReleaseStrategy ?
                                `Runway will automatically release your app ${whenToAutoRelease(
                                    autoReleaseStrategy,
                                )} once it has been approved by the App Store Review team.`
                            :   'You will manually release your app to users once it has been approved by the App Store Review team.';
                    case AppleAppStoreReleaseType.Scheduled:
                        if (appleStoreData.versionReviewSubmissionItem?.earliestReleaseDate) {
                            const releaseDate = dayjs(
                                appleStoreData.versionReviewSubmissionItem?.earliestReleaseDate,
                            ).format(DatetimeFormat.DayMonthDateYear);

                            return `Your app is scheduled to be released on ${releaseDate}, pending approval by the App Store Review team.`;
                        } else {
                            return 'Your app is scheduled to be released on the scheduled date, pending approval by the App Store Review team.';
                        }
                    default:
                        return autoReleaseStrategy ?
                                `Runway will automatically release your app ${whenToAutoRelease(
                                    autoReleaseStrategy,
                                )} once it has been submitted and reviewed.`
                            :   'Your app will be ready to release once it has been submitted and reviewed.';
                }
            case ReleaseStepStatus.Pending:
                if (appleStoreData?.versionReviewSubmissionItem?.state === AppleAppStoreState.ProcessingForAppStore) {
                    return 'Your app is processing for the App Store and will be available to all users shortly.';
                }

                switch (appleStoreData?.versionReviewSubmissionItem?.releaseType) {
                    case AppleAppStoreReleaseType.AfterApproval:
                        return 'Your update will be made available to all users as soon as it is approved by Apple.';
                    case AppleAppStoreReleaseType.Manual:
                        return autoReleaseStrategy ?
                                `Your update is ready to be released. Runway will automatically release your app update ${whenToAutoRelease(
                                    autoReleaseStrategy,
                                )}.`
                            :   'Your update is ready to be released.';
                    case AppleAppStoreReleaseType.Scheduled:
                        if (appleStoreData.versionReviewSubmissionItem?.earliestReleaseDate) {
                            const releaseDate = dayjs(
                                appleStoreData.versionReviewSubmissionItem?.earliestReleaseDate,
                            ).format(DatetimeFormat.DayMonthDateYear);

                            return autoReleaseStrategy ?
                                    `Runway will automatically release your update on ${releaseDate}.`
                                :   `Your app is scheduled to be released on ${releaseDate}.`;
                        } else {
                            return autoReleaseStrategy ?
                                    `Runway will automatically release your update ${whenToAutoRelease(
                                        autoReleaseStrategy,
                                    )}.`
                                :   'Your update is scheduled to be released on the scheduled date.';
                        }
                    default:
                        return '';
                }
            case ReleaseStepStatus.PendingChecklistOnly: // not possible to be ready but not released
            case ReleaseStepStatus.Ready:
            case ReleaseStepStatus.Blocked: // not a possible state currently
                // Currently not possible
                return '';
            default:
                return '';
        }
    }
};

const whenToAutoRelease = (autoReleaseStrategy: AutoReleaseStrategy) => {
    switch (autoReleaseStrategy) {
        case AutoReleaseStrategy.OnDate:
            return 'on or after your target release date';
        case AutoReleaseStrategy.OnceStepsComplete:
            return 'when all steps are complete';
        case AutoReleaseStrategy.OnceStepsCompleteAfterTargetDate:
            return 'on or after your target release date when all steps are complete';
    }
};
